// Helper Functions for the Asset Table Components

import { TableCellInputType } from "@components/data-table/types";
import { AssetTableColumnFromApi } from "@hooks/use-config-context/asset/types";
import { FieldsConfigResponse } from "@hooks/use-config-context/types";
import { getTableColumnsFromFields } from "@hooks/use-config/asset/utils";
import {
  FieldOptions,
  WpBoolean,
  WpFieldConfig,
} from "@hooks/use-config/types";
import { WpDamagesObj } from "@hooks/use-get/assets/types";
import { WpTaxonomyValue } from "@hooks/use-get/taxonomy-values";
import { formatDate as sharedFormatDate, parseDateInput } from "@utils/format-utils";

export type FormTab = {
  id: number;
  name: string;
  label: string;
  fields: Record<string, any>;
};

type StateValueFromField = {
  id: string;
  name: string;
  label: string;
  inputType: TableCellInputType;
  options: Record<string, string> | FieldOptions | [];
  required: boolean;
  taxonomyOptions: string[] | WpTaxonomyValue[] | [];
  subFields: WpFieldConfig[] | [];
};

export const createFormFields = (
  data: any[],
  dataConfig: FieldsConfigResponse,
): Record<string, any> => {
  return data.reduce(
    (acc, column) => {
      const field = getStateValuesFromField(column, dataConfig);

      acc[field.id] = field;

      if (field.subFields && field.subFields.length > 0) {
        field.subFields
          .filter((sf) => sf.id !== "notes_date" &&
            sf.id !== "notes_creator" &&
            sf.id !== "damage_ticket_id" &&
            sf.id !== "damage_ticket_href" &&
            sf.id !== "damage_ticket_number"
          )
          .forEach((subField) => {
            acc[subField.id] = {
              isSubField: true,
              ...getStateValuesFromField(
                getTableColumnsFromFields({ [subField.id]: subField })[0],
                dataConfig,
              ),
            };
          });
      }

      return acc;
    },
    {} as Record<string, any>,
  );
};

export const createFormTabs = (
  tableColumns: any[],
  dataConfig: FieldsConfigResponse,
  firstTabName: string,
  firstTabLabel: string,
): FormTab[] => {
  const formTabs: FormTab[] = [
    {
      id: 0,
      name: firstTabName,
      label: firstTabLabel,
      fields: {},
    },
  ];

  tableColumns.forEach((column) => {
    //* Sort Non Clone Fields into their own Tab
    if (column.clone === false) {
      const field = getStateValuesFromField(column, dataConfig);
      formTabs[0].fields[field.id] = field;
    }

    //* Sorting Clone Fields
    if (column.clone === true) {
      /*
       * Exception Handling
       *"asset_to_user_to" needs
       * to be sorted into the first tab with the non clone fields
       */
      if (column.id === "asset_to_user_to") {
        const field = getStateValuesFromField(column, dataConfig);
        formTabs[0].fields[field.id] = field;
      }

      //* Sort each other clone field into its own tab
      if (
        column.clone === true &&
        column.id !== "asset_to_user_to" &&
        column.id !== "ticket_to_asset_from" &&
        column.id !== "ticket_notes_repeater"
      ) {
        const field = getStateValuesFromField(column, dataConfig);

        const newTab: FormTab = {
          id: formTabs.length,
          name: field.id,
          label: field.label,
          fields: {},
        };

        field.subFields
          .filter((sf) => sf.id !== "notes_date" &&
            sf.id !== "notes_creator" &&
            sf.id !== "damage_ticket_id" &&
            sf.id !== "damage_ticket_href" &&
            sf.id !== "damage_ticket_number"
          )
          .forEach((sf) => {
            const subFieldWithValues = getStateValuesFromField(
              getTableColumnsFromFields({
                [sf.id]: sf,
              }).find((data) => data.id === sf.id) as AssetTableColumnFromApi,
              dataConfig,
            );

            newTab.fields[sf.id] = subFieldWithValues;
          });

        formTabs.push(newTab);
      }
    }
  });

  return formTabs;
};

export const createInitialState = (
  formFields: Record<string, any>,
  initialValues: Partial<Record<TableCellInputType, string>>,
  editData?: any,
) => {
  return Object.values(formFields).reduce((acc, column) => {
    let value = initialValues[column.inputType as TableCellInputType] ?? "";

    if (editData && editData.id) {
      if (column.id === "title") {
        value = editData.title?.rendered || "";
      } else if (column.isSubField) {
        const parentField = Object.values(formFields).find((f) =>
          f.subFields?.some((sf: any) => sf.id === column.id),
        );

        // * Handle case where parent field value is an array (e.g. clone fields)
        if (parentField && editData.meta_box?.[parentField.id] !== undefined) {
          const parentValue = editData.meta_box[parentField.id];

          if (Array.isArray(parentValue)) {
            value = parentValue[parentValue.length - 1]?.[column.id] ?? "";
          } else if (parentValue && typeof parentValue === "object") {
            value = parentValue[column.id] ?? "";
          }
        }

        if (parentField && editData.meta_box?.[parentField.id]) {
          value = editData.meta_box[parentField.id][column.id] ?? value;
        }
      } else if (
        editData.meta_box &&
        editData.meta_box[column.id] !== undefined
      ) {
        value = editData.meta_box[column.id];

        // Normalize user relationship field to a primitive ID for select controls.
        if (column.id === "asset_to_user_to") {
          if (Array.isArray(value)) {
            const firstValue = value[0];
            value =
              firstValue && typeof firstValue === "object"
                ? (firstValue as Record<string, any>).id ?? ""
                : firstValue ?? "";
          } else if (value && typeof value === "object") {
            value = (value as Record<string, any>).id ?? "";
          }
        }
      }

      //* Handle Taxonomy Objs
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        // Repeating Field Exceptions Below - these should remain arrays
        column.id !== "damages_group_field" &&
        column.id !== "notes_repeater" &&
        column.id !== "ticket_to_asset_from"
      ) {
        value = value[0];
      }

      //* Normalize Boolean Values
      if (column.inputType === TableCellInputType.Boolean) {
        const normalizedBool = String(value).toLowerCase();
        if (normalizedBool === "1" || normalizedBool === "true" || normalizedBool === "yes") {
          value = WpBoolean.YES;
        } else {
          value = WpBoolean.NO;
        }
      }
    }

    return {
      ...acc,
      [column.id]: value,
    };
  }, {});
};

export const createModalUrl = (id: string | number, endpoint: string) => {
  const splitUrl = window.location.href.split("/");
  return `${splitUrl[0]}//${splitUrl[2]}/${endpoint}/${id}`;
};

export const copyModalLinkToClipboard = (
  id: string | number,
  endpoint: string,
  setAlertOpen: (open: boolean) => void,
  setAlertMsg: (msg: string) => void,
) => {
  const modalUrl = createModalUrl(id, endpoint);

  navigator.clipboard.writeText(modalUrl);

  setAlertMsg(`${modalUrl} copied to clipboard`);
  setAlertOpen(true);
  setTimeout(() => {
    setAlertOpen(false);
    setAlertMsg("");
  }, 5000); // Alert closes after 5 seconds
};

export const getStateValuesFromField = (
  field: AssetTableColumnFromApi,
  dataConfig: FieldsConfigResponse,
): StateValueFromField => {
  const baseOptions = field.options || dataConfig[field.id]?.options || [];

  const options = field.id === "asset_to_user_to"
    ? Array.isArray(baseOptions)
      ? [{ label: "Unassign User", value: "" }, ...baseOptions]
      : {
        "": "Unassign User",
        ...baseOptions,
      }
    : baseOptions;

  return {
    id: field.id,
    name: field.id,
    label: field.label,
    inputType: field.inputType,
    options,
    required: field.required || false,
    taxonomyOptions: dataConfig[field.id]?.taxonomy ?? [],
    subFields: dataConfig[field.id]?.fields || [],
  };
};

export const formatDate = sharedFormatDate;

export const orderEntriesByDate = (entries: WpDamagesObj[]) => {
  return sortDamageEntries(entries);
};

type DamageEntry = Partial<WpDamagesObj> & {
  [key: string]: unknown;
};

const asNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

export const toTicketId = (row: DamageEntry): number | null => {
  const rawId = row.damage_ticket_id;

  if (typeof rawId === "number" && Number.isInteger(rawId) && rawId > 0) {
    return rawId;
  }

  if (typeof rawId === "string") {
    const normalized = Number(rawId.trim());
    if (Number.isInteger(normalized) && normalized > 0) {
      return normalized;
    }
  }

  // Transition-safe fallback during migration only.
  const href = asNonEmptyString(row.damage_ticket_href);
  if (!href) return null;
  const match = href.match(/\/tickets\/(\d+)(?:\/)?$/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const toTicketUrl = (
  ticketId: number | null,
  frontendBaseUrl: string,
): string | null => {
  if (!ticketId || !Number.isInteger(ticketId) || ticketId <= 0) return null;
  const origin = frontendBaseUrl.replace(/\/$/, "");
  return `${origin}/tickets/${ticketId}`;
};

export const isPlaceholderDamageRow = (row: DamageEntry): boolean => {
  const href = asNonEmptyString(row.damage_ticket_href) ?? "";
  const endsWithTicketZero = /\/tickets\/0\/?$/.test(href);
  if (!endsWithTicketZero) return false;

  const hasDate = !!asNonEmptyString(row.damages_time);
  const hasReport = !!asNonEmptyString(row.damage_report);
  const hasTicketNumber = !!asNonEmptyString(row.damage_ticket_number);
  const hasFile = Array.isArray(row.damage_file_upload)
    && row.damage_file_upload.some((f) => !!asNonEmptyString(f));

  return !hasDate && !hasReport && !hasTicketNumber && !hasFile;
};

export const sortDamageEntries = (entries: DamageEntry[]): DamageEntry[] => {
  return entries
    .map((entry, idx) => ({ entry, idx }))
    .sort((a, b) => {
      const hasDateA = !!asNonEmptyString(a.entry.damages_time);
      const hasDateB = !!asNonEmptyString(b.entry.damages_time);

      if (hasDateA !== hasDateB) return hasDateA ? -1 : 1;

      if (hasDateA && hasDateB) {
        const parsedA = parseDateInput(a.entry.damages_time as string);
        const parsedB = parseDateInput(b.entry.damages_time as string);
        const tsA = parsedA ? parsedA.getTime() : Number.NaN;
        const tsB = parsedB ? parsedB.getTime() : Number.NaN;
        const safeA = Number.isNaN(tsA) ? Number.NEGATIVE_INFINITY : tsA;
        const safeB = Number.isNaN(tsB) ? Number.NEGATIVE_INFINITY : tsB;
        if (safeA !== safeB) return safeB - safeA;
      }

      const hasReportA = !!asNonEmptyString(a.entry.damage_report);
      const hasReportB = !!asNonEmptyString(b.entry.damage_report);
      if (hasReportA !== hasReportB) return hasReportA ? -1 : 1;

      return a.idx - b.idx;
    })
    .map(({ entry }) => entry);
};

export const getDisplayDamageEntries = (entries: DamageEntry[]): DamageEntry[] => {
  const filtered = entries.filter((row) => !isPlaceholderDamageRow(row));
  return sortDamageEntries(filtered);
};
